/**
 * @architecture-header 1
 * @layer frontend-package
 * @domain contracts
 * @responsibility Define validated cross-runtime error, identity, Activity, upload, learning, and event contracts.
 * @state-owner none
 * @allowed-imports Zod
 * @forbidden React; DOM; application state
 * @public-api @vispo/contracts
 * @side-effects none
 * @tests contracts.test.ts
 */

import { z } from 'zod';

const apiErrorWireSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.unknown().optional(),
  request_id: z.string(),
});

export const apiErrorSchema = apiErrorWireSchema.transform(({ request_id, ...error }) => ({
  ...error,
  requestId: request_id,
}));

export type ApiError = z.infer<typeof apiErrorSchema>;

export const roleSchema = z.enum(['teacher', 'student']);
export type Role = z.infer<typeof roleSchema>;

export const activeContextSchema = z.object({
  id: z.string().min(1),
  organizationId: z.string().min(1),
  classId: z.string().nullable(),
  role: roleSchema,
});

export type ActiveContext = z.infer<typeof activeContextSchema>;

export const accountRoleEntrySchema = z.object({
  role: z.string(),
  is_current: z.boolean(),
});

export const accountRolesResponseSchema = z.object({
  current_role: z.string(),
  roles: z.array(accountRoleEntrySchema),
});

export type AccountRolesResponse = z.infer<typeof accountRolesResponseSchema>;

export const organizationContextSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  logo_url: z.string().nullable(),
  timezone: z.string(),
  kind: z.enum(['personal', 'institution']),
  membership_role: z.enum(['owner', 'admin', 'staff', 'member']),
  is_classroom_educator: z.boolean(),
  revision: z.number().int().min(1),
});

export const profileContextSchema = z.object({
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  revision: z.number().int().positive(),
});

export const localeSchema = z.enum(['zh', 'en']);
export type Locale = z.infer<typeof localeSchema>;

export const preferencesContextSchema = z.object({
  locale: localeSchema,
});

export const meContextResponseSchema = z.object({
  profile: profileContextSchema.nullable(),
  preferences: preferencesContextSchema.nullable(),
  organizations: z.array(organizationContextSchema),
});

export type ProfileContext = z.infer<typeof profileContextSchema>;
export type PreferencesContext = z.infer<typeof preferencesContextSchema>;
export type MeContextResponse = z.infer<typeof meContextResponseSchema>;

export const updateProfileInputSchema = z
  .object({
    displayName: z.string().trim().min(1).max(120).optional(),
    clearAvatar: z.boolean().optional(),
    expectedRevision: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict()
  .refine(
    ({ displayName, clearAvatar }) => displayName !== undefined || clearAvatar !== undefined,
    {
      message: 'displayName or clearAvatar is required',
    },
  );

export const updateProfileResponseSchema = z
  .object({
    profile: profileContextSchema,
  })
  .strict();

export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;
export type UpdateProfileResponse = z.infer<typeof updateProfileResponseSchema>;

export const updatePreferencesInputSchema = z
  .object({
    locale: localeSchema,
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();

export const updatePreferencesResponseSchema = z
  .object({
    preferences: preferencesContextSchema,
  })
  .strict();

export type UpdatePreferencesInput = z.infer<typeof updatePreferencesInputSchema>;
export type UpdatePreferencesResponse = z.infer<typeof updatePreferencesResponseSchema>;

export const updateOrganizationInputSchema = z
  .object({
    name: z.string().trim().min(1).max(160),
    clearLogo: z.boolean().optional(),
    expectedRevision: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();

export const updateOrganizationResponseSchema = z
  .object({
    organization: organizationContextSchema,
  })
  .strict();

export type UpdateOrganizationInput = z.infer<typeof updateOrganizationInputSchema>;
export type UpdateOrganizationResponse = z.infer<typeof updateOrganizationResponseSchema>;
export type OrganizationContext = z.infer<typeof organizationContextSchema>;

// This operation is a deliberate exception to this file's own camelCase-on-the-wire convention:
// services/api/src/vispo_api/modules/identity_access/schemas.py's OrganizationPasswordChangeCommand
// / OrganizationSessionResponse carry no Field(alias=...) at all (StrictContract's own
// model_config has no alias_generator either), unlike every other command in that file --
// confirmed by direct read, not assumed. Snake case here is not a typo to "fix" toward camelCase;
// it is what change_organization_password actually accepts and returns on the wire.
export const changeOrganizationPasswordInputSchema = z
  .object({
    current_password: z.string().min(1).max(64),
    new_password: z.string().min(8).max(64),
  })
  .strict();

export const organizationSessionResponseSchema = z
  .object({
    status: z.literal('authenticated'),
    user_id: z.string(),
    csrf_token: z.string().min(32),
    expires_at: z.string(),
  })
  .strict();

export type ChangeOrganizationPasswordInput = z.infer<typeof changeOrganizationPasswordInputSchema>;
export type OrganizationSessionContext = z.infer<typeof organizationSessionResponseSchema>;

export const organizationMemberRoleSchema = z.enum(['owner', 'admin', 'staff', 'member']);
export const organizationMemberStateSchema = z.enum(['active', 'left', 'removed']);
export const organizationMemberSchema = z.object({
  membershipId: z.string().uuid(),
  userId: z.string().uuid(),
  displayName: z.string(),
  role: organizationMemberRoleSchema,
  state: organizationMemberStateSchema,
  joinedAt: z.string(),
  endedAt: z.string().nullable(),
});
export const organizationMemberDirectorySchema = z
  .object({
    organizationId: z.string().uuid(),
    revision: z.number().int().positive(),
    members: z.array(organizationMemberSchema),
  })
  .strict();
export const organizationMemberCommandResponseSchema = z
  .object({
    organizationId: z.string().uuid(),
    revision: z.number().int().positive(),
    membership: organizationMemberSchema,
  })
  .strict();
export type OrganizationMemberRole = z.infer<typeof organizationMemberRoleSchema>;
export type OrganizationMemberState = z.infer<typeof organizationMemberStateSchema>;
export type OrganizationMember = z.infer<typeof organizationMemberSchema>;
export type OrganizationMemberDirectory = z.infer<typeof organizationMemberDirectorySchema>;
export type OrganizationMemberCommandResponse = z.infer<
  typeof organizationMemberCommandResponseSchema
>;
export const organizationMemberCreateInputSchema = z
  .object({
    userId: z.string().uuid(),
    role: organizationMemberRoleSchema,
    expectedRevision: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();
export const organizationMemberUpdateInputSchema = z
  .object({
    role: organizationMemberRoleSchema.optional(),
    state: z.enum(['active', 'removed']).optional(),
    expectedRevision: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict()
  .refine(({ role, state }) => role !== undefined || state !== undefined, {
    message: 'role or state is required',
  });
export type OrganizationMemberCreateInput = z.infer<typeof organizationMemberCreateInputSchema>;
export type OrganizationMemberUpdateInput = z.infer<typeof organizationMemberUpdateInputSchema>;

export const organizationInvitationRoleSchema = z.enum(['admin', 'staff', 'member']);
export const organizationInvitationStatusSchema = z.enum([
  'pending',
  'accepted',
  'declined',
  'revoked',
  'expired',
]);
// token is present only on the create/resend response that just minted it -- absent everywhere
// else, exactly once per rotation (CL-F03's token-secrecy contract).
export const organizationInvitationSchema = z
  .object({
    id: z.string().uuid(),
    recipientEmail: z.string(),
    invitedRole: organizationInvitationRoleSchema,
    status: organizationInvitationStatusSchema,
    revision: z.number().int().positive(),
    expiresAt: z.string(),
    token: z.string().optional(),
  })
  .strict();
export type OrganizationInvitationRole = z.infer<typeof organizationInvitationRoleSchema>;
export type OrganizationInvitationStatus = z.infer<typeof organizationInvitationStatusSchema>;
export type OrganizationInvitation = z.infer<typeof organizationInvitationSchema>;

export const organizationInvitationListResponseSchema = z
  .object({
    organizationId: z.string().uuid(),
    invitations: z.array(organizationInvitationSchema),
  })
  .strict();
export type OrganizationInvitationListResponse = z.infer<
  typeof organizationInvitationListResponseSchema
>;

export const organizationInvitationCommandResponseSchema = z
  .object({
    organizationId: z.string().uuid(),
    invitation: organizationInvitationSchema,
  })
  .strict();
export type OrganizationInvitationCommandResponse = z.infer<
  typeof organizationInvitationCommandResponseSchema
>;

export const organizationInvitationCreateInputSchema = z
  .object({
    recipientEmail: z.string().trim().min(1).max(320),
    invitedRole: organizationInvitationRoleSchema,
    expiresAt: z.string().min(1),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();
export type OrganizationInvitationCreateInput = z.infer<
  typeof organizationInvitationCreateInputSchema
>;

export const organizationInvitationRevisionInputSchema = z
  .object({
    expectedRevision: z.number().int().positive(),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();
export type OrganizationInvitationRevisionInput = z.infer<
  typeof organizationInvitationRevisionInputSchema
>;

// Acceptance-side: unauthenticated preview, then authenticated accept/decline. Never scoped by an
// organizationId the client supplies -- the token alone resolves the invitation and its org.
export const organizationInvitationPreviewInputSchema = z
  .object({ token: z.string().min(1) })
  .strict();
export type OrganizationInvitationPreviewInput = z.infer<
  typeof organizationInvitationPreviewInputSchema
>;

export const organizationInvitationPreviewResponseSchema = z
  .object({
    id: z.string().uuid(),
    organizationName: z.string(),
    invitedRole: organizationInvitationRoleSchema,
    expiresAt: z.string(),
  })
  .strict();
export type OrganizationInvitationPreviewResponse = z.infer<
  typeof organizationInvitationPreviewResponseSchema
>;

export const organizationInvitationAcceptInputSchema = z
  .object({
    token: z.string().min(1),
    idempotencyKey: z.string().trim().min(1).max(200),
  })
  .strict();
export type OrganizationInvitationAcceptInput = z.infer<
  typeof organizationInvitationAcceptInputSchema
>;

export const organizationInvitationAcceptResponseSchema = z
  .object({
    membershipId: z.string().uuid(),
    organizationId: z.string().uuid(),
    role: organizationInvitationRoleSchema,
  })
  .strict();
export type OrganizationInvitationAcceptResponse = z.infer<
  typeof organizationInvitationAcceptResponseSchema
>;

export const examRowSchema = z
  .object({
    id: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
    total_points: z.number().nullable().optional(),
    pdf_storage_path: z.string().nullable().optional(),
    pdf_url: z.string().nullable().optional(),
    answer_pdf_storage_path: z.string().nullable().optional(),
    answer_pdf_url: z.string().nullable().optional(),
    answer_key: z.record(z.string(), z.unknown()).nullable().optional(),
    exam_layout: z.unknown().nullable().optional(),
    created_at: z.string().nullable().optional(),
    updated_at: z.string().nullable().optional(),
  })
  .loose();

export type ExamRow = z.infer<typeof examRowSchema>;

export const uploadTargetSchema = z.object({
  uploadSessionId: z.string().min(1),
  bucket: z.string().min(1),
  storagePath: z.string().min(1),
  token: z.string().min(1),
  signedUploadUrl: z.url(),
  expiresIn: z.number().int().positive(),
  status: z.literal('pending'),
});

export type UploadTarget = z.infer<typeof uploadTargetSchema>;

export const uploadStatusSchema = z
  .object({
    uploadSessionId: z.string().min(1),
    domain: z.string(),
    bucket: z.string().min(1),
    storagePath: z.string().min(1),
    status: z.enum(['pending', 'ready', 'failed', 'expired']),
    fileName: z.string(),
    contentType: z.string(),
    failureReason: z.string().nullable().optional(),
    artifact: z.record(z.string(), z.unknown()).nullable().optional(),
  })
  .loose();

export type UploadStatus = z.infer<typeof uploadStatusSchema>;

const activityUuidSchema = z.string().uuid();

export const createdActivitySchema = z.object({
  id: activityUuidSchema,
  organization_id: activityUuidSchema,
  classroom_id: activityUuidSchema.nullable(),
  curriculum_unit_id: activityUuidSchema.nullable(),
  title: z.string().min(1),
  workflow_status: z.enum(['needs_review', 'ready']),
  visibility: z.enum(['private', 'organization']),
  revision: z.number().int().positive(),
  item_count: z.number().int().nonnegative(),
  published_version_id: activityUuidSchema.nullable(),
});

export type CreatedActivity = z.infer<typeof createdActivitySchema>;

export const workspaceActivitiesSchema = z.object({
  organization_id: activityUuidSchema,
  items: z.array(
    z.object({
      id: activityUuidSchema,
      organization_id: activityUuidSchema,
      classroom_id: activityUuidSchema.nullable(),
      title: z.string(),
      access_kind: z.enum(['admin', 'owner', 'classroom', 'share', 'organization', 'assignment']),
      visibility: z.enum(['private', 'organization']).nullable(),
      workflow_status: z.enum(['processing', 'needs_review', 'ready']).nullable(),
      revision: z.number().int().positive().nullable(),
      published_version_id: activityUuidSchema.nullable(),
      published_version_no: z.number().int().positive().nullable(),
      item_count: z.number().int().nonnegative(),
      needs_review_count: z.number().int().nonnegative(),
      teacher_user_ids: z.array(activityUuidSchema),
      updated_at: z.string().nullable(),
    }),
  ),
  share_recipients: z.array(z.unknown()),
});

export type WorkspaceActivities = z.infer<typeof workspaceActivitiesSchema>;

export const pairedIngestSubmitSchema = z.object({
  job_id: z.string().min(1),
  status: z.string(),
});

export const pairedIngestResultSchema = z
  .object({
    exam_id: z.string().min(1),
    exam_class_file_id: z.string().min(1),
    question_count: z.number().int().nonnegative(),
    group_count: z.number().int().nonnegative(),
    review_required: z.boolean().optional().default(false),
    publish_state: z.string().optional().default('publishable'),
    review_reasons: z.array(z.string()).optional().default([]),
  })
  .loose();

export const pairedIngestStatusSchema = z
  .object({
    status: z.string(),
    result: pairedIngestResultSchema.nullable().optional(),
    error: z.string().nullable().optional(),
    warning: z.string().nullable().optional(),
    review_required: z.boolean().optional().default(false),
    publish_state: z.string().nullable().optional(),
    review_reasons: z.array(z.string()).optional().default([]),
    hints_total: z.number().int().nullable().optional(),
    hints_completed: z.number().int().nullable().optional(),
  })
  .loose();

export type PairedIngestStatus = z.infer<typeof pairedIngestStatusSchema>;

export const assignExamResponseSchema = z.object({
  assignments: z.array(
    z.object({
      class_id: z.string(),
      classwork_item_id: z.string(),
      attachment_id: z.string(),
      dispatched: z.number().int().nonnegative(),
    }),
  ),
  count: z.number().int().nonnegative(),
});

export const recallPreviewSchema = z
  .object({
    item_id: z.string(),
    active_count: z.number().int().nonnegative(),
    not_started_count: z.number().int().nonnegative(),
    in_progress_count: z.number().int().nonnegative(),
    submitted_count: z.number().int().nonnegative(),
    graded_count: z.number().int().nonnegative(),
    confirmation_level: z.enum(['none', 'standard', 'submitted']),
    requires_confirmation: z.boolean(),
  })
  .loose();

export type RecallPreview = z.infer<typeof recallPreviewSchema>;

export const recallResultSchema = z
  .object({
    message: z.string(),
    recalled: z.number().int().nonnegative().optional(),
  })
  .loose();

/**
 * The one seat vocabulary, declared at the wire boundary so the API, both hosts and the product
 * cannot drift apart. `SeatStatus` in workspace-web is derived from this, never re-declared.
 */
export const seatStatusSchema = z.enum(['offline', 'online', 'ok', 'stuck', 'need']);

export type SeatStatusValue = z.infer<typeof seatStatusSchema>;

export const interventionEventSchema = z.object({
  roomId: z.string(),
  studentId: z.string(),
  status: seatStatusSchema,
  questionIndex: z.number().int().nonnegative(),
  occurredAt: z.string(),
});

export type InterventionEvent = z.infer<typeof interventionEventSchema>;

export const reportWeakTopicSchema = z
  .object({
    name: z.string().min(1),
    accuracy: z.number().int().min(0).max(100).nullable().optional(),
    error_rate: z.number().nullable().optional(),
    correct: z.number().int().nonnegative().nullable().optional(),
    total: z.number().int().nonnegative().nullable().optional(),
  })
  .loose();

export const reportSummarySchema = z.object({
  student_name: z.string().min(1),
  period_start: z.string().min(1),
  period_end: z.string().min(1),
  study_hours: z.string(),
  accuracy_pct: z.number().int().min(0).max(100),
  exams_completed: z.number().int().nonnegative(),
  questions_answered: z.number().int().nonnegative(),
  weak_topics: z.array(reportWeakTopicSchema),
  ai_suggestions: z.array(z.string()),
});

export const generatedReportResponseSchema = z.object({
  report: reportSummarySchema,
  lang: z.string(),
  student_id: z.string().min(1),
  range: z.string(),
});

export type GeneratedReportResponse = z.infer<typeof generatedReportResponseSchema>;

export const weakTopicsResponseSchema = z.object({
  weak_topics: z.array(reportWeakTopicSchema),
  student_id: z.string().min(1),
  range: z.string(),
  total_topics_analyzed: z.number().int().nonnegative().nullable().optional(),
});

export const teacherStudentSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    grade: z.string().nullable().optional(),
    currentLevel: z.number(),
    xpPoints: z.number(),
    streakDays: z.number(),
    totalSessions: z.number().int().nonnegative(),
    correctAnswers: z.number().int().nonnegative(),
    totalQuestions: z.number().int().nonnegative(),
    lastSessionDate: z.string().nullable().optional(),
    progressTrend: z.array(
      z.object({
        date: z.string(),
        xp: z.number(),
        correctRate: z.number(),
        sessionsCompleted: z.number(),
      }),
    ),
  })
  .loose();

export const reportSendResponseSchema = z.object({
  success: z.boolean(),
  resend_id: z.string().nullable().optional(),
  recipient: z.email(),
  subject: z.string().min(1),
});

export const inboxItemSchema = z
  .object({
    id: z.string().min(1),
    type: z.enum(['submission', 'enrollment', 'overdue', 'graded', 'announcement']),
    title: z.string(),
    subtitle: z.string(),
    timestamp: z.string(),
    isRead: z.boolean(),
    isCompleted: z.boolean(),
    isUrgent: z.boolean(),
    meta: z
      .object({
        submissionId: z.string().nullable().optional(),
        classworkItemId: z.string().nullable().optional(),
        examId: z.string().nullable().optional(),
        classId: z.string().nullable().optional(),
        className: z.string().nullable().optional(),
        studentId: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .loose();

export type InboxItem = z.infer<typeof inboxItemSchema>;

export const classFileSchema = z
  .object({
    id: z.string().min(1),
    class_id: z.string().min(1),
    teacher_id: z.string().min(1),
    file_type: z.enum(['document', 'exam']),
    name: z.string().min(1),
    mime_type: z.string().nullable(),
    size_bytes: z.number().int().nonnegative().nullable(),
    storage_path: z.string().nullable(),
    signed_url: z.url().nullable(),
    exam_id: z.string().nullable(),
    folder_path: z.string(),
    status: z.enum(['ready', 'uploading', 'parsing', 'error', 'deleted']),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .loose();

export type ClassFile = z.infer<typeof classFileSchema>;

export const reportDraftSchema = z
  .object({
    id: z.string().min(1),
    content: z.string(),
    status: z.string(),
    kind: z.string(),
    student_id: z.string(),
  })
  .loose();

export const reportDraftEnvelopeSchema = z.object({
  draft: reportDraftSchema,
});
